import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import utility functions
import { formatCurrency, formatPercentage } from './lib/utils'

// Import UI components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Separator } from './components/ui/separator';
import { Checkbox } from './components/ui/checkbox';

// Import icons
import { 
  DollarSign, 
  User, 
  CreditCard, 
  PiggyBank, 
  TrendingUp, 
  Plus, 
  Minus,
  Users,
  FileText,
  Settings,
  LogOut,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  Filter,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useInfiniteReveal } from './hooks/use-infinite-reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility functions

// Treat bare ISO like "YYYY-MM-DDTHH:mm:ss[.sss]" as UTC
const parseISOAssumingUTC = (value) => {
    if (!value) return null;
    if (typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
        return new Date(value + 'Z'); // assume UTC if no offset/Z
    }
    return new Date(value); // already has Z/offset or is Date
};

// Mirrors the server's past-due rule (next_due_date has passed and there is an outstanding
// balance) so views can derive freshness locally — the persisted is_past_due flag is only
// re-synced by the parent-only /accounts/past-due endpoint (#41/#51).
const isAccountPastDue = (account) => {
  if (!account?.next_due_date || !(account.balance > 0)) return false;
  const due = parseISOAssumingUTC(account.next_due_date);
  return due instanceof Date && !Number.isNaN(due.getTime()) && due < new Date();
};

// Format the date and time using the correct timezone settings
const formatDateTime = (value, userTimezone = 'America/Los_Angeles') => {
  if (!value) return '';
  const d = parseISOAssumingUTC(value);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
};

const tzOffsetMinutesAt = (utcLikeDate, userTimezone) => {
    const asShownInTZ = new Date(utcLikeDate.toLocaleString('en-US', { timeZone: userTimezone }));
    const asShownInLocal = new Date(utcLikeDate.toLocaleString('en-US', { timeZone: 'UTC' }));
    return (asShownInTZ.getTime() - asShownInLocal.getTime()) / 60000;

}

// For <input type="datetime-local"> value (YYYY-MM-DDTHH:mm) from a UTC ISO string
export const formatDateTimeForInput = (value, userTimezone = 'America/Los_Angeles') => {
    if (!value) return '';
    // Convert the UTC instant to the target timezone "wall time", then format parts
    const dInTZ = new Date(parseISOAssumingUTC(value).toLocaleString('en-US', { timeZone: userTimezone }));
    const y = dInTZ.getFullYear();
    const m = String(dInTZ.getMonth() + 1).padStart(2, '0');
    const d = String(dInTZ.getDate()).padStart(2, '0');
    const hh = String(dInTZ.getHours()).padStart(2, '0');
    const mm = String(dInTZ.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}`;
};

// Parse a datetime-local string (assumed in userTimezone) and return a UTC ISO string
export const convertToUTC = (localDateString, userTimezone = 'America/Los_Angeles') => {
    // localDateString is like "2025-10-23T14:30" representing the user's chosen timezone
    if (!localDateString) return null;

    // Turn "YYYY-MM-DDTHH:mm" into numeric parts
    const [datePart, timePart] = localDateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0] = (timePart || '00:00').split(':').map(Number);

    // Build a Date as if those components were UTC (we'll shift by the timezone's offset)
    const asIfUTC = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

    // Find the timezone's offset at that instant, then subtract it to land on the true UTC instant
    const offsetMin = tzOffsetMinutesAt(asIfUTC, userTimezone);
    const trueUTCms = asIfUTC.getTime() - offsetMin * 60000;
    return new Date(trueUTCms).toISOString();
};

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const getCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to get current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token, getCurrentUser]);

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, user } = response.data;
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'child'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const result = await login({
          username: formData.username,
          password: formData.password
        });
        if (!result.success) {
          setError(result.error);
        }
      } else {
        const result = await register(formData);
        if (result.success) {
          setIsLogin(true);
          setError('');
          setFormData({
            username: '',
            password: '',
            email: '',
            first_name: '',
            last_name: '',
            role: 'child'
          });
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0 bg-white/80 backdrop-blur-sm">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mb-4">
            <PiggyBank className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Family Banking
          </CardTitle>
          <CardDescription className="text-slate-600">
            {isLogin ? 'Sign in to your account' : 'Create a new account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                required
                className="h-11"
              />
            </div>

            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="h-11"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

        </CardContent>
      </Card>
    </div>
  );
};

// Parent Dashboard Components
const ParentDashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  // null until /accounts/past-due has answered successfully; then the authoritative list
  const [pastDueAccounts, setPastDueAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showCreateTransaction, setShowCreateTransaction] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showEditTransaction, setShowEditTransaction] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // The server-side filters currently applied to the transactions list (null = none).
  // fetchData() re-applies them on every refetch, and the endless scroll restarts from
  // the top only when they change — not on CRUD refetches (#24)
  const [appliedFilters, setAppliedFilters] = useState(null);
  // Bumped by Apply/Clear so each always issues a fetch (a retry after a failed request
  // would otherwise be a state no-op); it is an extra trigger of the fetch effect below.
  const [reloadNonce, setReloadNonce] = useState(0);
  // Sequence number of the latest fetchData call: a response from a superseded call
  // (e.g. Apply A, then Apply B before A finishes) is ignored so the latest wins.
  const fetchSeq = useRef(0);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [timezone, setTimezone] = useState(localStorage.getItem('timezone') || 'America/Los_Angeles');
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showArchivedItems, setShowArchivedItems] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'account'|'user', id: string, name: string}
  const [archiveTarget, setArchiveTarget] = useState(null); // {type: 'account'|'user', id: string, name: string, action: 'archive'|'restore'}
  const [selectedAccountFilter, setSelectedAccountFilter] = useState(null);
  const [selectedUserFilter, setSelectedUserFilter] = useState(null);
  const [newAccount, setNewAccount] = useState({
    user_id: '',
    account_type: 'loan',
    name: '',
    description: '',
    interest_rate: 0,
    compounding_period: 'monthly',
    payment_amount: 0,
    due_date_rule: null,
    auto_calculate_interest: true
  });
  
  const [editAccount, setEditAccount] = useState({});
  
  const [filters, setFilters] = useState({
    start_date: null,
    end_date: null,
    account_ids: [],
    user_ids: [],
    transaction_types: []
  });
  
  const [editTransaction, setEditTransaction] = useState({});
  
  const [editUser, setEditUser] = useState({});
  const [newTransaction, setNewTransaction] = useState({
    account_id: '',
    transaction_type: 'charge',
    amount: 0,
    description: '',
    transaction_date: null
  });

  // Single fetch path for the dashboard's data. Depends on the archive toggle and the
  // applied server-side filters, so the effect below refetches exactly when those change
  // (and once on mount); CRUD handlers call it directly after their writes.
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const [accountsRes, childrenRes, transactionsRes, auditLogsRes, pastDueRes] = await Promise.all([
        axios.get(`${API}/accounts?include_archived=${showArchivedItems}`),
        axios.get(`${API}/users/children?include_archived=${showArchivedItems}`),
        // Keep the applied server-side filters across refetches (CRUD, archive toggle)
        appliedFilters
          ? axios.post(`${API}/transactions/filter`, appliedFilters)
          : axios.get(`${API}/transactions`),
        axios.get(`${API}/audit-logs?limit=50`).catch(() => ({ data: [] })),
        // Also keeps is_past_due in sync server-side (#41); null marks a failed request so a
        // successful empty list can still override stale account flags
        axios.get(`${API}/accounts/past-due`).catch(() => ({ data: null }))
      ]);
      if (seq !== fetchSeq.current) return; // superseded by a newer fetch — ignore this response
      setAccounts(accountsRes.data);
      setUsers(childrenRes.data); // Use children data for the users state
      setTransactions(transactionsRes.data);
      setAuditLogs(auditLogsRes.data);
      // null (failed request) marks the sync unavailable — consumers then derive the state
      // from the fresh account fields instead of trusting a leftover list
      setPastDueAccounts(pastDueRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [showArchivedItems, appliedFilters]);

  // Fetch on mount, whenever fetchData's inputs change (archive toggle, applied filters),
  // and whenever Apply/Clear asks for a reload.
  useEffect(() => {
    fetchData();
  }, [fetchData, reloadNonce]);

  const createAccount = async () => {
    try {
      await axios.post(`${API}/accounts`, newAccount);
      setShowCreateAccount(false);
      setNewAccount({
        user_id: '',
        account_type: 'loan',
        name: '',
        description: '',
        interest_rate: 0,
        compounding_period: 'monthly',
        payment_amount: 0,
        due_date_rule: null,
        auto_calculate_interest: true
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create account:', error);
    }
  };

  const updateAccount = async () => {
    try {
      await axios.put(`${API}/accounts/${selectedAccount.id}`, editAccount);
      setShowEditAccount(false);
      setSelectedAccount(null);
      setEditAccount({});
      fetchData();
    } catch (error) {
      console.error('Failed to update account:', error);
    }
  };

  const updateTransaction = async () => {
    try {
      // Debug logging
      console.log('Updating transaction:', selectedTransaction.id);
      console.log('Edit data:', editTransaction);
      
      // Prepare the update data, ensuring dates are properly formatted
      const updateData = { ...editTransaction };
      if (updateData.transaction_date && typeof updateData.transaction_date === 'string') {
        // Ensure the date is in ISO format
        updateData.transaction_date = new Date(updateData.transaction_date).toISOString();
      }
      
      console.log('Sending update data:', updateData);
      
      const response = await axios.put(`${API}/transactions/${selectedTransaction.id}`, updateData);
      
      console.log('Update response:', response.data);
      
      setShowEditTransaction(false);
      setSelectedTransaction(null);
      setEditTransaction({});
      fetchData();
      
      // Show success message
      alert('Transaction updated successfully!');
      
    } catch (error) {
      console.error('Failed to update transaction:', error);
      console.error('Error response:', error.response?.data);
      alert('Failed to update transaction: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
    }
  };

  const deleteTransaction = async (transactionId) => {
    if (window.confirm('Are you sure you want to delete this transaction? This will recalculate all subsequent balances.')) {
      try {
        await axios.delete(`${API}/transactions/${transactionId}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete transaction:', error);
      }
    }
  };

  const createTransaction = async () => {
    try {
      await axios.post(`${API}/transactions`, newTransaction);
      setShowCreateTransaction(false);
      setNewTransaction({
        account_id: '',
        transaction_type: 'charge',
        amount: 0,
        description: '',
        transaction_date: null
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create transaction:', error);
    }
  };

  // Opening the New Transaction dialog applies the active click filter (#59): lock to the
  // selected account, or clear a remembered account that isn't offered by the dropdown —
  // whether it belongs to another member or is absent from the fetched set entirely (e.g.
  // archived and hidden), so nothing can be posted to an account the parent can't see.
  // Done at open time so filter changes take effect on the next open and never disturb an
  // already-open dialog.
  const handleCreateTransactionOpenChange = (open) => {
    if (open) {
      if (lockedAccount) {
        setNewTransaction(prev => ({ ...prev, account_id: lockedAccount.id }));
      } else {
        setNewTransaction(prev =>
          !prev.account_id || dialogAccounts.some(a => a.id === prev.account_id)
            ? prev
            : { ...prev, account_id: '' }
        );
      }
    }
    setShowCreateTransaction(open);
  };

  // Applying/clearing server-side filters only updates `appliedFilters` (+ the reload nonce);
  // fetchData (via its effect) reloads the list with them, so there is one fetch path. The
  // nonce guarantees a request even when the selection is unchanged (retry after a failure).
  const applyFilters = () => {
    setAppliedFilters(filters);
    setReloadNonce((n) => n + 1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      start_date: null,
      end_date: null,
      account_ids: [],
      user_ids: [],
      transaction_types: []
    });
    // Also clear click filters
    setSelectedAccountFilter(null);
    setSelectedUserFilter(null);
    setAppliedFilters(null);
    setReloadNonce((n) => n + 1);
  };

  const calculateInterest = async (accountId, effectiveDate = null) => {
    try {
      const payload = effectiveDate ? { effective_date: effectiveDate } : {};
      await axios.post(`${API}/accounts/${accountId}/calculate-interest`, payload);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Failed to calculate interest:', error);
    }
  };

  const openEditAccount = (account) => {
    setSelectedAccount(account);
    setEditAccount({
      name: account.name,
      description: account.description,
      interest_rate: account.interest_rate,
      compounding_period: account.compounding_period,
      payment_amount: account.payment_amount,
      due_date_rule: account.due_date_rule,
      auto_calculate_interest: account.auto_calculate_interest
    });
    setShowEditAccount(true);
  };

  const openEditTransaction = (transaction) => {
    console.log('Opening edit for transaction:', transaction);
    setSelectedTransaction(transaction);
    setEditTransaction({
      transaction_type: transaction.transaction_type,
      amount: transaction.amount,
      description: transaction.description,
      transaction_date: transaction.transaction_date || transaction.created_at
    });
    setShowEditTransaction(true);
  };

  const openEditUser = (user) => {
    setSelectedUser(user);
    setEditUser({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email
    });
    setShowEditUser(true);
  };

  const updateUser = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editUser);
      setShowEditUser(false);
      setSelectedUser(null);
      setEditUser({});
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Failed to update family member: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const openDeleteConfirmation = (type, item) => {
    setDeleteTarget({
      type: type,
      id: item.id,
      name: type === 'account' ? item.name : `${item.first_name} ${item.last_name}`
    });
    setDeleteConfirmationText('');
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (deleteConfirmationText !== 'DELETE') {
      alert('You must type DELETE in all caps to confirm');
      return;
    }

    try {
      const endpoint = deleteTarget.type === 'account' 
        ? `${API}/accounts/${deleteTarget.id}`
        : `${API}/users/${deleteTarget.id}`;
      
      await axios.delete(endpoint, {
        data: { confirmation: 'DELETE' }
      });
      
      setShowDeleteConfirmation(false);
      setDeleteTarget(null);
      setDeleteConfirmationText('');
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Failed to delete: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const canDeleteUser = (user) => {
    const userAccounts = accounts.filter(a => a.user_id === user.id && !a.is_archived);
    return userAccounts.length === 0;
  };

  const saveTimezoneSettings = () => {
    localStorage.setItem('timezone', timezone);
    setShowSettings(false);
    // Refresh data to update displayed times
    fetchData();
  };

  const canArchiveUser = (user) => {
    // Get ALL accounts for this user (including archived ones)
    const userAccounts = accounts.filter(a => a.user_id === user.id);
    const activeAccounts = userAccounts.filter(a => !a.is_archived);
    
    // User can be archived if they have no active accounts
    // (they can have archived accounts)
    return activeAccounts.length === 0;
  };

  const handleAccountFilter = (accountId) => {
    if (selectedAccountFilter === accountId) {
      // If clicking the same account, deselect it
      setSelectedAccountFilter(null);
      setFilters({
        ...filters,
        account_ids: [],
        user_ids: selectedUserFilter ? [selectedUserFilter] : []
      });
    } else {
      // Select this account (independently of family member selection)
      setSelectedAccountFilter(accountId);
      setFilters({
        ...filters,
        account_ids: [accountId],
        user_ids: selectedUserFilter ? [selectedUserFilter] : []
      });
    }
  };

  const handleUserFilter = (userId) => {
    if (selectedUserFilter === userId) {
      // If clicking the same user, deselect it
      setSelectedUserFilter(null);
      setFilters({
        ...filters,
        user_ids: [],
        account_ids: selectedAccountFilter ? [selectedAccountFilter] : []
      });
    } else {
      // Select this user
      setSelectedUserFilter(userId);
      
      // Check if the currently selected account belongs to this user
      // If not, clear the account selection since it won't be visible anymore
      let newAccountFilter = selectedAccountFilter;
      if (selectedAccountFilter) {
        const selectedAccount = accounts.find(a => a.id === selectedAccountFilter);
        if (selectedAccount && selectedAccount.user_id !== userId) {
          // Account doesn't belong to this user, so clear it
          newAccountFilter = null;
          setSelectedAccountFilter(null);
        }
      }
      
      setFilters({
        ...filters,
        user_ids: [userId],
        account_ids: newAccountFilter ? [newAccountFilter] : []
      });
    }
  };

  // Filter accounts based on selected user
  const filteredAccounts = selectedUserFilter 
    ? accounts.filter(account => account.user_id === selectedUserFilter)
    : accounts;

  // New Transaction dialog scoping (#59): an active account click filter locks the dialog
  // to that account (only if it is in the fetched set — e.g. not hidden by the archive
  // toggle); otherwise a family-member filter narrows the dropdown to their accounts.
  const lockedAccount = selectedAccountFilter
    ? accounts.find(a => a.id === selectedAccountFilter) || null
    : null;
  const dialogAccounts = lockedAccount
    ? [lockedAccount]
    : selectedUserFilter
      ? accounts.filter(a => a.user_id === selectedUserFilter)
      : accounts;

  // Past-due accounts to surface, restricted to ones visible under the current archive
  // toggle (/accounts respects showArchivedItems, /accounts/past-due does not). When the
  // sync is unavailable (null — failed or not yet run), derive from the fresh account
  // fields with the same rule the server applies.
  const visiblePastDueAccounts = pastDueAccounts !== null
    ? pastDueAccounts.filter(p => accounts.some(a => a.id === p.id))
    : accounts.filter(isAccountPastDue);

  // Filter transactions based on all active filters (click filters + backend filters).
  // Memoised on its real inputs so the array keeps its identity across unrelated re-renders —
  // useInfiniteReveal resets the revealed count whenever this identity changes (#23).
  const filteredTransactions = useMemo(() => {
    let result = transactions;

    // Apply click filters (selectedAccountFilter or selectedUserFilter)
    if (selectedAccountFilter) {
      // If specific account is selected, show only its transactions
      result = result.filter(transaction => transaction.account_id === selectedAccountFilter);
    } else if (selectedUserFilter) {
      // If user is selected, show transactions for all their accounts
      const userAccountIds = accounts
        .filter(account => account.user_id === selectedUserFilter)
        .map(account => account.id);
      result = result.filter(transaction => userAccountIds.includes(transaction.account_id));
    }

    // Note: Backend filters are already applied to the transactions array via applyFilters()
    // The transactions state contains pre-filtered data from the server
    // So we only need to apply the click filters on top of that

    return result;
  }, [transactions, accounts, selectedAccountFilter, selectedUserFilter]);

  // Endless scroll: reveal the filtered list 10 rows at a time. Restart from the top only
  // when a click filter or the applied server-side filters change — CRUD refetches keep the
  // revealed rows and scroll position (#24).
  const { visibleItems: visibleTransactions, hasMore: hasMoreTx, sentinelRef } = useInfiniteReveal(filteredTransactions, {
    resetKey: [selectedAccountFilter, selectedUserFilter, JSON.stringify(appliedFilters)].join('|'),
  });

  const openArchiveConfirmation = (type, item, action = 'archive') => {
    setArchiveTarget({
      type: type,
      id: item.id,
      name: type === 'account' ? item.name : `${item.first_name} ${item.last_name}`,
      action: action
    });
    setShowArchiveConfirmation(true);
  };

  const confirmArchive = async () => {
    try {
      const endpoint = archiveTarget.type === 'account' 
        ? `${API}/accounts/${archiveTarget.id}/archive`
        : `${API}/users/${archiveTarget.id}/archive`;
      
      await axios.put(endpoint, { archive: archiveTarget.action === 'archive' });
      
      setShowArchiveConfirmation(false);
      setArchiveTarget(null);
      fetchData();
    } catch (error) {
      console.error('Failed to archive:', error);
      alert('Failed to archive: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const children = users.filter(user => user.role === 'child');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Family Banking</h1>
                <p className="text-sm text-slate-600">Parent Dashboard</p>
              </div>
            </div>
            <div className="header-buttons-container">
              <Dialog open={showCreateAccount} onOpenChange={setShowCreateAccount}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-xs lg:text-sm">
                    <Plus className="w-4 h-4 lg:mr-2" />
                    <span className="hidden md:inline lg:hidden">Account</span>
                    <span className="hidden lg:inline">New Account</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Account</DialogTitle>
                    <DialogDescription>Create a new account for a family member</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Family Member</Label>
                      <Select value={newAccount.user_id} onValueChange={(value) => setNewAccount({...newAccount, user_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select family member" />
                        </SelectTrigger>
                        <SelectContent>
                          {children.map(child => (
                            <SelectItem key={child.id} value={child.id}>
                              {child.first_name} {child.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Type</Label>
                      <Select value={newAccount.account_type} onValueChange={(value) => setNewAccount({...newAccount, account_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="loan">Loan</SelectItem>
                          <SelectItem value="bill">Bill</SelectItem>
                          <SelectItem value="rent">Rent</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Name</Label>
                      <Input 
                        value={newAccount.name} 
                        onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                        placeholder="e.g., Car Loan, Cell Phone Bill"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea 
                        value={newAccount.description} 
                        onChange={(e) => setNewAccount({...newAccount, description: e.target.value})}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Interest Rate (%)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={newAccount.interest_rate} 
                        onChange={(e) => setNewAccount({...newAccount, interest_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <Button onClick={createAccount} className="w-full">
                      Create Account
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showCreateTransaction} onOpenChange={handleCreateTransactionOpenChange}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="text-xs lg:text-sm">
                    <DollarSign className="w-4 h-4 lg:mr-2" />
                    <span className="hidden md:inline lg:hidden">Transaction</span>
                    <span className="hidden lg:inline">New Transaction</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Transaction</DialogTitle>
                    <DialogDescription>Record a payment or charge</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Account</Label>
                      <Select value={newTransaction.account_id} disabled={Boolean(lockedAccount)} onValueChange={(value) => setNewTransaction({...newTransaction, account_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent>
                          {dialogAccounts.map(account => {
                            const user = users.find(u => u.id === account.user_id);
                            return (
                              <SelectItem key={account.id} value={account.id}>
                                {user?.first_name} {user?.last_name} - {account.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Transaction Type</Label>
                      <Select value={newTransaction.transaction_type} onValueChange={(value) => setNewTransaction({...newTransaction, transaction_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="charge">Charge</SelectItem>
                          <SelectItem value="payment">Payment</SelectItem>
                          <SelectItem value="interest">Interest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={newTransaction.amount} 
                        onChange={(e) => setNewTransaction({...newTransaction, amount: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input 
                        value={newTransaction.description} 
                        onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                        placeholder="Transaction description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transaction Date (Optional)</Label>
                      <Input 
                        type="datetime-local"
                        value={newTransaction.transaction_date ? formatDateTimeForInput(newTransaction.transaction_date, timezone) : ''} 
                        onChange={(e) => setNewTransaction({...newTransaction, transaction_date: e.target.value ? convertToUTC(e.target.value, timezone) : null})}
                      />
                      <p className="text-xs text-slate-500">Leave blank to use current date/time. Time in your local timezone</p>
                    </div>
                    <Button onClick={createTransaction} className="w-full">
                      Create Transaction
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Edit Account Dialog */}
              <Dialog open={showEditAccount} onOpenChange={setShowEditAccount}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Account</DialogTitle>
                    <DialogDescription>Modify account settings and parameters</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Account Name</Label>
                      <Input 
                        value={editAccount.name || ''} 
                        onChange={(e) => setEditAccount({...editAccount, name: e.target.value})}
                        placeholder="e.g., Car Loan, Cell Phone Bill"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea 
                        value={editAccount.description || ''} 
                        onChange={(e) => setEditAccount({...editAccount, description: e.target.value})}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Interest Rate (%)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={editAccount.interest_rate || 0} 
                        onChange={(e) => setEditAccount({...editAccount, interest_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Amount</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={editAccount.payment_amount || 0} 
                        onChange={(e) => setEditAccount({...editAccount, payment_amount: parseFloat(e.target.value) || 0})}
                        placeholder="Monthly payment amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Compounding Period</Label>
                      <Select value={editAccount.compounding_period} onValueChange={(value) => setEditAccount({...editAccount, compounding_period: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annually">Annually</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={updateAccount} className="flex-1">
                        Update Account
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowEditAccount(false);
                          openDeleteConfirmation('account', selectedAccount);
                        }}
                        className="flex-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Account
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Edit Transaction Dialog */}
              <Dialog open={showEditTransaction} onOpenChange={setShowEditTransaction}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Transaction</DialogTitle>
                    <DialogDescription>Modify transaction details</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Transaction Type</Label>
                      <Select value={editTransaction.transaction_type} onValueChange={(value) => setEditTransaction({...editTransaction, transaction_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="charge">Charge</SelectItem>
                          <SelectItem value="payment">Payment</SelectItem>
                          <SelectItem value="interest">Interest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={editTransaction.amount || 0} 
                        onChange={(e) => setEditTransaction({...editTransaction, amount: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input 
                        value={editTransaction.description || ''} 
                        onChange={(e) => setEditTransaction({...editTransaction, description: e.target.value})}
                        placeholder="Transaction description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transaction Date</Label>
                      <Input 
                        type="datetime-local"
                        value={editTransaction.transaction_date ? formatDateTimeForInput(editTransaction.transaction_date, timezone) : ''} 
                        onChange={(e) => setEditTransaction({...editTransaction, transaction_date: e.target.value ? convertToUTC(e.target.value, timezone) : null})}
                      />
                      <p className="text-xs text-slate-500">Time shown in your local timezone</p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={updateTransaction} className="flex-1">
                        Update Transaction
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowEditTransaction(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Transaction Filters Dialog */}
              <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Filter Transactions</DialogTitle>
                    <DialogDescription>Apply filters to view specific transactions</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input 
                        type="date"
                        value={filters.start_date ? new Date(filters.start_date).toISOString().slice(0, 10) : ''} 
                        onChange={(e) => setFilters({...filters, start_date: e.target.value ? new Date(e.target.value).toISOString() : null})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input 
                        type="date"
                        value={filters.end_date ? new Date(filters.end_date).toISOString().slice(0, 10) : ''} 
                        onChange={(e) => setFilters({...filters, end_date: e.target.value ? new Date(e.target.value).toISOString() : null})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transaction Types</Label>
                      <div className="flex gap-2 flex-wrap">
                        {['payment', 'charge', 'interest'].map(type => (
                          <div key={type} className="flex items-center space-x-2">
                            <Checkbox 
                              id={type}
                              checked={filters.transaction_types.includes(type)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFilters({...filters, transaction_types: [...filters.transaction_types, type]});
                                } else {
                                  setFilters({...filters, transaction_types: filters.transaction_types.filter(t => t !== type)});
                                }
                              }}
                            />
                            <Label htmlFor={type} className="capitalize">{type}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={applyFilters} className="flex-1">
                        Apply Filters
                      </Button>
                      <Button variant="outline" onClick={clearFilters} className="flex-1">
                        Clear
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Audit Log Dialog */}
              <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>Audit Log</DialogTitle>
                    <DialogDescription>System activity and change history</DialogDescription>
                  </DialogHeader>
                  <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {auditLogs.map(log => (
                        <div key={log.id} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-sm">
                                <Badge variant="secondary" className="mr-2">{log.action}</Badge>
                                {log.description}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {formatDateTime(log.timestamp, timezone)} • {log.entity_type}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {auditLogs.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          No audit entries found
                        </div>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Edit Family Member Dialog */}
              <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Family Member</DialogTitle>
                    <DialogDescription>Update family member information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input 
                        value={editUser.first_name || ''} 
                        onChange={(e) => setEditUser({...editUser, first_name: e.target.value})}
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input 
                        value={editUser.last_name || ''} 
                        onChange={(e) => setEditUser({...editUser, last_name: e.target.value})}
                        placeholder="Last name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={editUser.email || ''} 
                        onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                        placeholder="Email address"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={updateUser} className="flex-1">
                        Update Family Member
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowEditUser(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Delete Confirmation Dialog */}
              <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center text-red-600">
                      <AlertTriangle className="w-5 h-5 mr-2" />
                      Confirm Deletion
                    </DialogTitle>
                    <DialogDescription>
                      This action cannot be undone and will permanently delete all associated data.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="font-semibold text-red-800 mb-2">⚠️ WARNING</h4>
                      <p className="text-sm text-red-700 mb-2">
                        You are about to permanently delete:
                      </p>
                      <p className="font-medium text-red-800">
                        {deleteTarget?.type === 'account' ? 'Account' : 'Family Member'}: {deleteTarget?.name}
                      </p>
                      {deleteTarget?.type === 'account' && (
                        <p className="text-sm text-red-600 mt-2">
                          This will also delete all transactions associated with this account.
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-red-700">
                        Type "DELETE" in all caps to confirm:
                      </Label>
                      <Input 
                        value={deleteConfirmationText}
                        onChange={(e) => setDeleteConfirmationText(e.target.value)}
                        placeholder="Type DELETE here"
                        className="border-red-300 focus:border-red-500"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={confirmDelete}
                        disabled={deleteConfirmationText !== 'DELETE'}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Permanently
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowDeleteConfirmation(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Archive Confirmation Dialog */}
              <Dialog open={showArchiveConfirmation} onOpenChange={setShowArchiveConfirmation}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center">
                      {archiveTarget?.action === 'archive' ? '📁' : <RotateCcw className="w-5 h-5 mr-2" />}
                      {archiveTarget?.action === 'archive' ? 'Archive' : 'Restore'} {archiveTarget?.type === 'account' ? 'Account' : 'Family Member'}
                    </DialogTitle>
                    <DialogDescription>
                      {archiveTarget?.action === 'archive' 
                        ? `This will hide the ${archiveTarget?.type} from normal view but preserve all data.`
                        : `This will restore the ${archiveTarget?.type} to active status.`
                      }
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">
                        <strong>{archiveTarget?.action === 'archive' ? 'Archive' : 'Restore'}:</strong> {archiveTarget?.name}
                      </p>
                      {archiveTarget?.action === 'archive' && (
                        <p className="text-xs text-blue-600 mt-2">
                          You can restore this {archiveTarget?.type} later using the "Show Archived" button.
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={confirmArchive}
                        className="flex-1"
                      >
                        {archiveTarget?.action === 'archive' ? '📁 Archive' : <><RotateCcw className="w-4 h-4 mr-2" />Restore</>}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowArchiveConfirmation(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Settings Dialog */}
              <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center">
                      <Settings className="w-5 h-5 mr-2" />
                      Settings
                    </DialogTitle>
                    <DialogDescription>Configure your application preferences</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Timezone</Label>
                      <Select value={timezone} onValueChange={setTimezone}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/Los_Angeles">Pacific Time (Los Angeles)</SelectItem>
                          <SelectItem value="America/Denver">Mountain Time (Denver)</SelectItem>
                          <SelectItem value="America/Chicago">Central Time (Chicago)</SelectItem>
                          <SelectItem value="America/New_York">Eastern Time (New York)</SelectItem>
                          <SelectItem value="America/Phoenix">Arizona Time (Phoenix)</SelectItem>
                          <SelectItem value="America/Anchorage">Alaska Time (Anchorage)</SelectItem>
                          <SelectItem value="Pacific/Honolulu">Hawaii Time (Honolulu)</SelectItem>
                          <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
                          <SelectItem value="Europe/London">GMT (London)</SelectItem>
                          <SelectItem value="Europe/Paris">Central European Time (Paris)</SelectItem>
                          <SelectItem value="Asia/Tokyo">Japan Time (Tokyo)</SelectItem>
                          <SelectItem value="Asia/Shanghai">China Time (Shanghai)</SelectItem>
                          <SelectItem value="Australia/Sydney">Australian Eastern Time (Sydney)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Current time: {new Date().toLocaleString('en-US', { timeZone: timezone })}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={saveTimezoneSettings} className="flex-1">
                        Save Settings
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowSettings(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {visiblePastDueAccounts.length > 0 && (
          <Alert className="mb-6 border-red-300 bg-red-50 text-red-800">
            <AlertTriangle className="h-4 w-4 !text-red-600" />
            <AlertDescription>
              <span className="font-semibold">
                {visiblePastDueAccounts.length} account{visiblePastDueAccounts.length === 1 ? '' : 's'} past due:
              </span>{' '}
              {visiblePastDueAccounts.map(a => a.name).join(', ')}
            </AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Accounts */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Accounts Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredAccounts.map(account => {
                    const user = users.find(u => u.id === account.user_id);
                    const isSelected = selectedAccountFilter === account.id;
                    // Single source of truth: the authoritative synced list when available,
                    // otherwise the locally derived state (see visiblePastDueAccounts).
                    const isPastDue = visiblePastDueAccounts.some(p => p.id === account.id);
                    return (
                      <div 
                        key={account.id} 
                        className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-200' 
                            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                        }`}
                        onClick={() => handleAccountFilter(account.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-800">
                                {account.name}
                              </h3>
                              {isSelected && (
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">
                              {user?.first_name} {user?.last_name}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="secondary">
                                {account.account_type}
                              </Badge>
                              {isPastDue && (
                                <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50">
                                  Past due
                                </Badge>
                              )}
                              {account.is_archived && (
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                  Archived
                                </Badge>
                              )}
                            </div>
                            {account.description && (
                              <p className="text-xs text-slate-500 mt-1">{account.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(account.balance)}
                            </div>
                            {account.interest_rate > 0 && (
                              <div className="text-sm text-slate-600">
                                {formatPercentage(account.interest_rate)} APR
                              </div>
                            )}
                            {account.payment_amount > 0 && (
                              <div className="text-sm text-slate-600">
                                Payment: {formatCurrency(account.payment_amount)}
                              </div>
                            )}
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {account.interest_rate > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    calculateInterest(account.id);
                                  }}
                                >
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  Interest
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditAccount(account);
                                }}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openArchiveConfirmation('account', account, account.is_archived ? 'restore' : 'archive');
                                }}
                                className="h-7 text-xs"
                                title={account.is_archived ? "Restore account" : "Archive account"}
                              >
                                {account.is_archived ? <RotateCcw className="w-3 h-3" /> : '📁'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Family Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-700">Total Assets</div>
                    <div className="text-lg font-bold text-green-800">
                      {formatCurrency(accounts.filter(a => a.balance > 0).reduce((sum, a) => sum + a.balance, 0))}
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="text-sm text-red-700">Total Owed</div>
                    <div className="text-lg font-bold text-red-800">
                      {formatCurrency(Math.abs(accounts.filter(a => a.balance < 0).reduce((sum, a) => sum + a.balance, 0)))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-slate-800">Family Members</h4>
                    </div>
                    <div className="space-y-3">
                      {children.map(child => {
                        const isSelected = selectedUserFilter === child.id;
                        return (
                          <div key={child.id} className="p-3 border rounded-lg">
                            <div className="flex justify-between items-start">
                              <div 
                                className={`flex-1 cursor-pointer transition-all duration-200 ${
                                  isSelected 
                                    ? 'bg-blue-50 -m-3 p-3 rounded-lg border-2 border-blue-400 shadow-lg' 
                                    : 'hover:bg-slate-50 -m-3 p-3 rounded-lg'
                                }`}
                                onClick={() => handleUserFilter(child.id)}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-sm">{child.first_name} {child.last_name}</div>
                                  {isSelected && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">{child.email}</div>
                                <div className="flex gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {accounts.filter(a => a.user_id === child.id).length} accounts
                                  </Badge>
                                  {child.is_archived && (
                                    <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                                      Archived
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1 ml-3">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditUser(child);
                                  }}
                                  className="h-7 text-xs px-2"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openArchiveConfirmation('user', child, child.is_archived ? 'restore' : 'archive');
                                  }}
                                  disabled={!child.is_archived && !canArchiveUser(child)}
                                  className="h-7 text-xs px-2"
                                  title={!child.is_archived && !canArchiveUser(child) ? "Archive all accounts first" : child.is_archived ? "Restore family member" : "Archive family member"}
                                >
                                  {child.is_archived ? <RotateCcw className="w-3 h-3" /> : '📁'}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirmation('user', child);
                                  }}
                                  disabled={!canDeleteUser(child)}
                                  className="h-7 text-xs px-2 text-red-600 hover:text-red-700"
                                  title={!canDeleteUser(child) ? "Delete or archive all accounts first" : "Delete family member"}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Transactions */}
        <Card className="mt-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Recent Transactions
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowFilters(true)}
                  className="text-xs lg:text-sm"
                >
                  <Filter className="w-3 h-3 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden lg:inline">Filters</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAuditLog(true)}
                  className="text-xs lg:text-sm"
                >
                  <FileText className="w-3 h-3 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden lg:inline">Audit Log</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowArchivedItems(!showArchivedItems)}
                  className={`text-xs lg:text-sm ${showArchivedItems ? "bg-blue-50 border-blue-300" : ""}`}
                >
                  <Eye className="w-3 h-3 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden lg:inline">{showArchivedItems ? 'Hide' : 'Show'} Archived</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="text-xs lg:text-sm"
                >
                  <Settings className="w-3 h-3 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden lg:inline">Settings</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {visibleTransactions.map(transaction => {
                const account = accounts.find(a => a.id === transaction.account_id);
                const user = users.find(u => u.id === account?.user_id);
                return (
                  <div key={transaction.id} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{transaction.description}</div>
                      <div className="text-sm text-slate-600">
                        {user?.first_name} {user?.last_name} - {account?.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatDateTime(transaction.transaction_date || transaction.created_at, timezone)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${
                        transaction.transaction_type === 'payment' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.transaction_type === 'payment' ? '-' : '+'}{formatCurrency(Math.abs(transaction.amount))}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {transaction.transaction_type}
                      </Badge>
                      <div className="flex gap-1 mt-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditTransaction(transaction)}
                          className="h-7 text-xs"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => deleteTransaction(transaction.id)}
                          className="h-7 text-xs text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredTransactions.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No transactions found for the selected filter
                </div>
              )}
              {/* Sentinel + end bar — only when there is something to scroll (#26) */}
              {filteredTransactions.length > 0 && (hasMoreTx ? (
                  <div ref={sentinelRef} className="py-6 text-center text-sm text-slate-500">
                      Scroll to load more
                  </div>
              ) : (
                  <div className="mt-4 mb-6">
                      <div className="w-full bg-black text-white text-center py-2 rounded">
                          - End of Transactions -
                      </div>
                  </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Child Dashboard

const ChildDashboard = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timezone] = useState(localStorage.getItem('timezone') || 'America/Los_Angeles');
  const [selectedAccountFilter, setSelectedAccountFilter] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(`${API}/transactions`)
      ]);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountFilter = (accountId) => {
    if (selectedAccountFilter === accountId) {
      // If clicking the same account, deselect it
      setSelectedAccountFilter(null);
    } else {
      // Select this account
      setSelectedAccountFilter(accountId);
    }
  };

  // Filter transactions based on selected account.
  // Memoised so the array only changes identity when its inputs do (see ParentDashboard / #23).
  const filteredTransactions = useMemo(
    () => (selectedAccountFilter
      ? transactions.filter(transaction => transaction.account_id === selectedAccountFilter)
      : transactions),
    [transactions, selectedAccountFilter]
  );

  // Endless scroll: reveal the filtered list 10 rows at a time; restart only when the
  // account filter changes, not on data refetches (#24)
  const { visibleItems: visibleTransactions, hasMore: hasMoreTx, sentinelRef } = useInfiniteReveal(filteredTransactions, {
    resetKey: selectedAccountFilter,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const totalOwed = accounts.filter(a => 
    (a.account_type === 'loan' || a.account_type === 'bill' || a.account_type === 'rent') && a.balance > 0
  ).reduce((sum, a) => sum + Math.abs(a.balance), 0);
  
  const totalSavings = accounts.filter(a => 
    a.account_type === 'savings' && a.balance > 0
  ).reduce((sum, a) => sum + a.balance, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">
                  Welcome, {user?.first_name}!
                </h1>
                <p className="text-sm text-slate-600">Your Account Dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-gradient-to-r from-red-50 to-pink-50 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-700 text-sm font-medium">Total Owed</p>
                  <p className="text-2xl font-bold text-red-800">{formatCurrency(totalOwed)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <Minus className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-700 text-sm font-medium">Total Savings</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(totalSavings)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <PiggyBank className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Accounts */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Your Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {accounts.map(account => {
                const isSelected = selectedAccountFilter === account.id;
                return (
                  <div 
                    key={account.id} 
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-200' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                    onClick={() => handleAccountFilter(account.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{account.name}</h3>
                          {isSelected && (
                            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                          )}
                        </div>
                        {account.description && (
                          <p className="text-sm text-slate-600 mt-1">{account.description}</p>
                        )}
                        <Badge variant="secondary" className="mt-2">
                          {account.account_type}
                        </Badge>
                        {account.interest_rate > 0 && (
                          <Badge variant="outline" className="ml-2">
                            {formatPercentage(account.interest_rate)} APR
                          </Badge>
                        )}
                        {isAccountPastDue(account) && (
                          <Badge variant="outline" className="ml-2 text-red-600 border-red-300 bg-red-50">
                            Past due
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${
                          account.balance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(account.balance)}
                        </div>
                        {account.balance < 0 && (
                          <p className="text-sm text-red-600 mt-1">Amount Owed</p>
                        )}
                        {account.balance > 0 && account.account_type === 'savings' && (
                          <p className="text-sm text-green-600 mt-1">Savings Balance</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {visibleTransactions.map(transaction => {
                const account = accounts.find(a => a.id === transaction.account_id);
                return (
                  <div key={transaction.id} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{transaction.description}</div>
                      <div className="text-sm text-slate-600">{account?.name}</div>
                      <div className="text-xs text-slate-500">
                        {formatDateTime(transaction.transaction_date || transaction.created_at, timezone)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${
                        transaction.transaction_type === 'payment' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.transaction_type === 'payment' ? '-' : '+'}{formatCurrency(Math.abs(transaction.amount))}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {transaction.transaction_type}
                      </Badge>
                    </div>
                  </div>
                );
              })}
              {filteredTransactions.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No transactions found for the selected account
                </div>
              )}
              {/* Sentinel + end bar — only when there is something to scroll (#26) */}
              {filteredTransactions.length > 0 && (hasMoreTx ? (
                  <div ref={sentinelRef} className="py-6 text-center text-sm text-slate-500">
                      Scroll to load more
                  </div>
              ) : (
                  <div className="mt-4 mb-6">
                      <div className="w-full bg-black text-white text-center py-2 rounded">
                          - End of Transactions -
                      </div>
                  </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App Component
const Dashboard = () => {
  const { user, logout } = useAuth();

  return (
    <div className="relative">
      {/* Logout Button */}
      <div className="absolute top-4 right-2 lg:right-4 z-50">
        <Button 
          onClick={logout}
          variant="outline"
          size="sm"
          className="bg-white/80 backdrop-blur-sm text-xs lg:text-sm"
        >
          <LogOut className="w-4 h-4 lg:mr-2" />
          <span className="hidden lg:inline">Logout</span>
        </Button>
      </div>

      {user?.role === 'parent' ? <ParentDashboard /> : <ChildDashboard />}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

const LoginRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Login />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

export default App;